// Exam-integrity signals (additive, best-effort). Two jobs:
//  1. Tell the server the moment a question is opened, so it can measure
//     time-on-question authoritatively (closes the client-timer "0-trick").
//  2. Log tab blur / focus / visibility changes as soft proctoring signals.
// Every call is fire-and-forget: a failure here must NEVER disrupt the exam.
import { useEffect, useRef } from 'react';
import { apiPost } from '@/lib/api';

function emit(spId: string, type: string, questionId?: string): void {
  void apiPost(`/student-papers/${spId}/integrity`, { type, questionId }).catch(() => undefined);
}

export function useExamIntegrity(spId: string | null, activeQuestionId: string | null): void {
  const startedRef = useRef<Set<string>>(new Set());

  // Mark a question "started" the first time it's opened (idempotent server-side too).
  useEffect(() => {
    if (!spId || !activeQuestionId) return;
    if (startedRef.current.has(activeQuestionId)) return;
    startedRef.current.add(activeQuestionId);
    // Deliberate exception to "no writes from effects": question-start must
    // fire when the question OPENS (not on a click), is deduped by startedRef
    // here, and the server records it idempotently.
    // eslint-disable-next-line no-restricted-syntax -- see comment above
    void apiPost(`/student-papers/${spId}/questions/${activeQuestionId}/start`).catch(() => undefined);
  }, [spId, activeQuestionId]);

  // Tab visibility / focus signals.
  useEffect(() => {
    if (!spId) return;
    const onVisibility = (): void =>
      emit(spId, document.hidden ? 'visibility_hidden' : 'visibility_visible', activeQuestionId ?? undefined);
    const onBlur = (): void => emit(spId, 'blur', activeQuestionId ?? undefined);
    const onFocus = (): void => emit(spId, 'focus', activeQuestionId ?? undefined);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
    };
  }, [spId, activeQuestionId]);
}
